import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { isMacosPlatform } from '@utils/auth';
import { extraQueryParams } from '@utils/constants';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import NativeAuthError from './NativeAuthError';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { isLoading, isAuthenticated, signinRedirect } = useAuth();
  const { nativeAuthStatus } = useCommonState();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen text-xl font-extralight">
        {t('common.loading')}
      </div>
    );
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Inside the native macOS shell the tokens arrive through
  // `window.setAuthTokens` shortly after load. Redirecting to the hosted login
  // page would navigate the WebView away and destroy that bridge, so wait for
  // `useNativeBridgeAuth` to finish instead.
  if (isMacosPlatform()) {
    if (nativeAuthStatus === 'error') {
      return <NativeAuthError />;
    }

    return (
      <div className="flex items-center justify-center h-screen text-xl font-extralight">
        {t('auth.nativeAuthWaiting')}
      </div>
    );
  }

  const fullPath = window.location.pathname + window.location.search;
  signinRedirect({ extraQueryParams, state: { returnUrl: fullPath } }).catch(
    (error) => {
      console.error('Error during sign-in redirect:', error);
    },
  );
  return <div>{t('auth.redirectingToLogin')}</div>;
}

export default ProtectedRoute;
